<template>
    <FormInputContainer
        class="FormMultiSelect"
        :inputId="`I${_uid}`"
        :label="label"
        :errors="errors"
        :required="required"
    >
        <div ref="root" class="control">
            <!-- type="button" so opening the list never submits the surrounding form. -->
            <button
                :id="`I${_uid}`"
                ref="toggle"
                type="button"
                class="toggle"
                :class="{ isOpen, isEmpty: !selected.length }"
                :aria-expanded="isOpen ? 'true' : 'false'"
                aria-haspopup="true"
                @click="isOpen ? close() : open()"
                @keydown.esc.stop="close(true)"
            >
                <span class="summary">{{ summary }}</span>
                <Icon class="caret" name="chevron-down"/>
            </button>

            <div v-if="isOpen" class="menu" role="group" @keydown.esc.stop="close(true)">
                <label v-for="option in options" :key="option" class="option">
                    <input
                        type="checkbox"
                        :checked="isSelected(option)"
                        @change="toggleOption(option)"
                    />
                    <span>{{ option }}</span>
                </label>

                <p v-if="!options.length" class="noOptions">No options configured.</p>
            </div>
        </div>
    </FormInputContainer>
</template>


<script>
import FormInputContainer from './FormInputContainer';

export default {
    name: 'FormMultiSelect',
    inheritAttrs: false,

    components: { FormInputContainer },

    props: {
        value: { type: Array, default: () => [] },
        options: { type: Array, default: () => [] },
        label: { type: String },
        placeholder: { type: String, default: 'None' },
        errors: { type: Array },
        required: { type: Boolean, default: false },
    },

    data: () => ({
        isOpen: false,
    }),

    computed: {
        selected() {
            return this.value || [];
        },

        // Ordered by the configured options so the summary reads the same regardless of
        // the order the boxes were ticked in.
        summary() {
            const ordered = this.options.filter(option => this.isSelected(option));
            const extra = this.selected.filter(option => !this.options.includes(option));

            return [...ordered, ...extra].join(', ') || this.placeholder;
        },
    },

    beforeDestroy() {
        this.removeWindowCloseListener();
    },

    methods: {
        isSelected(option) {
            return this.selected.includes(option);
        },

        toggleOption(option) {
            const updated = this.isSelected(option)
                ? this.selected.filter(selected => selected !== option)
                : [...this.selected, option];

            // The menu deliberately stays open, so several boxes can be ticked in a row.
            this.$emit('input', updated);
        },

        addWindowCloseListener() {
            window.addEventListener('click', this.onWindowClick);
        },

        removeWindowCloseListener() {
            window.removeEventListener('click', this.onWindowClick);
        },

        // Unlike Dropdown, the menu itself has to be excluded too, or ticking a box
        // would close the list before a second one could be ticked.
        onWindowClick(event) {
            if (this.$refs.root && !this.$refs.root.contains(event.target)) {
                this.close();
            }
        },

        open() {
            this.isOpen = true;
            this.addWindowCloseListener();
        },

        close(refocus = false) {
            if (!this.isOpen) return;

            this.isOpen = false;
            this.removeWindowCloseListener();

            // Only on Escape: an outside click has its own target and must keep it.
            if (refocus && this.$refs.toggle) this.$refs.toggle.focus();
        },
    },
};
</script>


<style lang="scss" scoped>
@import 'globals.scss';
.FormMultiSelect {
    .control {
        position: relative;
    }

    .toggle {
        @extend %form-control;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        text-align: left;
        cursor: pointer;

        &.isEmpty .summary {
            color: rgba($text-color-muted, 0.75);
        }

        &.isOpen {
            border-color: $blue-color;
        }
    }

    .summary {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    .caret {
        flex: 0 0 auto;
        font-size: 12px;
        color: $text-color-muted;
    }

    .menu {
        position: absolute;
        z-index: $z-index-dropdown;
        top: calc(100% + 2px);
        left: 0;
        right: 0;
        max-height: 220px;
        overflow-y: auto;
        padding: 5px 0;
        border: 1px solid $divider-color;
        border-radius: $border-radius;
        background-color: $bg-color-1;
        box-shadow: $shadow-medium;
    }

    .option {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        padding: 7px $input-padding-horizontal;
        font-weight: $font-weight-regular;
        cursor: pointer;

        &:hover {
            background-color: $bg-color-3;
        }

        input {
            flex: 0 0 auto;
            margin: 0;
            cursor: pointer;
        }
    }

    .noOptions {
        margin: 0;
        padding: 7px $input-padding-horizontal;
        color: $text-color-muted;
    }
}
</style>
